import {
  CAMPAIGN_ENUM_TYPES,
  DocumentFormats,
  DMDocumentFormats,
  ADMDocumentFormats,
  CAMPAIGN_SUBTYPES_ENUM,
} from "shared/constants";

// Define regional country groupings based on flyer format availability
const ANZ_COUNTRIES = ["AU", "NZ"]; // Australia, New Zealand
const EU_COUNTRIES = ["FR", "NL", "UK", "BE", "CH", "DE", "ES", "IT", "PL", "LU", "PT"]; // European Union countries
const US_CA_COUNTRIES = ["US", "CA"]; // United States, Canada

/**
 * Validates flyer type based on regional country restrictions
 */
const validateCountryRestrictions = ({ countryCode, flyerType }) => {
  const isANZ = ANZ_COUNTRIES.includes(countryCode);
  const isEU = EU_COUNTRIES.includes(countryCode);
  const isUS_CA = US_CA_COUNTRIES.includes(countryCode);
  const anzEuFormats = [DocumentFormats.A4, DocumentFormats.A5, DocumentFormats.A6];
  const dmFormats = Object.values(DMDocumentFormats);
  const admFormats = Object.values(ADMDocumentFormats);

  // A4, A5, A6: Available in ANZ + EU regions only
  if (anzEuFormats.includes(flyerType)) {
    return isANZ || isEU;
  }

  // DL: Available in ANZ regions only
  if (flyerType === DocumentFormats.DL) {
    return isANZ;
  }

  // DM formats (SMALL, BEST_SELLER, LARGE, JUMBO, OVERSIZED): Available in US + CA regions only
  if ([...dmFormats, ...admFormats].includes(flyerType)) {
    return isUS_CA;
  }

  // Door hanger and Tearaway: Available in all countries
  if ([DocumentFormats.DOOR_HANGER, DocumentFormats.TEARAWAY].includes(flyerType)) {
    return true;
  }

  return false;
};

/**
 * Validates flyer type based on company channel-specific requirements
 */
const validateChannelRestrictions = ({ channel, flyerType, printingCosts, flyerWeight }) => {
  // Handle ADM campaigns - use ADM-specific document formats
  if (channel === CAMPAIGN_SUBTYPES_ENUM.ADM) {
    return Boolean(ADMDocumentFormats[flyerType]);
  }

  // Handle Direct Mail campaigns - use DM-specific document formats
  if ([CAMPAIGN_ENUM_TYPES.DIRECTMAIL, CAMPAIGN_SUBTYPES_ENUM.EDDM].includes(channel)) {
    return Boolean(DMDocumentFormats[flyerType]);
  }
  // Check if printing costs are available for this flyer type and weight
  const printingCostKey = flyerWeight ? `${flyerType}#${flyerWeight}` : flyerType;
  const hasPrintingCosts = Object.entries(printingCosts || {}).some(([key, costs]) => {
    return key.includes(printingCostKey) && costs.length > 0;
  });

  if (!hasPrintingCosts) {
    return false;
  }

  // Additional channel-specific validation for Tearaway
  if (flyerType === DocumentFormats.TEARAWAY) {
    return channel === CAMPAIGN_ENUM_TYPES.HANDTOHAND;
  }
  if (flyerType === DocumentFormats.DOOR_HANGER) {
    return channel === CAMPAIGN_ENUM_TYPES.LETTERBOX;
  }

  return true;
};

/**
 * Main validation function that orchestrates country and channel validations
 * @param {boolean} isPublicBuilder - If true, only run country restrictions. If false, run both country and channel restrictions.
 */
export const isValidFlyerType = ({
  countryCode,
  channel,
  printingCosts,
  flyerType,
  flyerWeight,
  isPublicBuilder = false,
}) => {
  // Always validate country restrictions first
  const passesCountryValidation = validateCountryRestrictions({ countryCode, flyerType });

  if (!passesCountryValidation) {
    return false;
  }

  // For public builders, only country restrictions apply
  if (isPublicBuilder) {
    return true;
  }

  // For internal builders, also validate channel restrictions
  return validateChannelRestrictions({ channel, flyerType, printingCosts, flyerWeight });
};

export const getFlyerWeightOptions = ({ flyerType, printingCosts }) => {
  const weightOptions = Object.entries(printingCosts).reduce((acc, [key, costs]) => {
    const strs = key.split("#");

    if (costs.length && strs.length === 2 && strs[0] === flyerType && !isNaN(strs[1])) {
      acc.push(strs[1]);
    }

    return acc;
  }, []);

  weightOptions.sort((a, b) => {
    const numA = Number(a);
    const numB = Number(b);

    if (numA < numB) {
      return -1;
    } else if (numA > numB) {
      return 1;
    } else {
      return 0;
    }
  });

  return weightOptions;
};
